import { useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { Icon, categoryIconName } from '../components/Icon';
import { theme } from '../theme';
import { fmtK, fmtMoney } from '../utils/format';
import { Aggregates, ApiError, Category, Operation } from '../api/types';

const RU_MON_NOM = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
function monthLabel(key: string): string {
  const [y, m] = key.split('-');
  return `${RU_MON_NOM[Number(m) - 1]} ${y}`;
}

/** Числа гроссбуха: Fraunces, tabular, без WONK. */
function fraunces(size: number): CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontVariationSettings: "'opsz' 144, 'WONK' 0",
    fontVariantNumeric: 'tabular-nums',
    fontSize: size,
    whiteSpace: 'nowrap',
  };
}

/**
 * Месяцы для пикера (от свежих к старым).
 *
 * Источник истины — серверный агрегат `op_months`: он знает всю историю, а окно
 * операций в bootstrap со временем сузится (план 3b), и пикер обязан не терять
 * месяцы, которых в окне уже нет. Месяцы окна всё равно добавляются сверху:
 * агрегат приходит снапшотом, а оптимистичная операция задним числом может
 * попасть в месяц, которого сервер ещё не видел. Без агрегата (старый снапшот
 * из IndexedDB, офлайн-старт) остаётся прежний расчёт по операциям.
 *
 * Текущий месяц есть в списке всегда: в первые дни месяца операций может не быть,
 * но открываться History должна на нём.
 */
export function historyMonths(
  ops: Operation[] = [],
  aggregates?: Aggregates,
  now: Date = new Date(),
): string[] {
  const set = new Set<string>(aggregates?.op_months ?? []);
  for (const op of ops) set.add(op.date.slice(0, 7));
  set.add(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  return Array.from(set).sort().reverse();
}

export function History() {
  const { viewData: data } = useBudget();

  const [fCat, setFCat] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [groupBy, setGroupBy] = useState<'day' | 'category'>('day');
  const [monthIdx, setMonthIdx] = useState(0);

  const months = useMemo(() => historyMonths(data?.operations, data?.aggregates), [data]);

  const monthKey = months[Math.min(monthIdx, months.length - 1)] ?? '';

  // операции выбранного месяца + фильтр по категории
  const filtered = useMemo(() => {
    if (!data) return [];
    return data.operations.filter((op) => {
      if (!op.date.startsWith(monthKey)) return false;
      if (fCat && op.category !== fCat) return false;
      return true;
    });
  }, [data, fCat, monthKey]);

  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    for (const op of filtered) {
      if (op.type === 'Доход') income += op.amount;
      else if (op.type === 'Расход') expense += op.amount;
    }
    return { income, expense, net: income - expense, count: filtered.length };
  }, [filtered]);

  const grouped = useMemo(() => {
    const map = new Map<string, Operation[]>();
    const sorted = [...filtered].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? 1 : -1;
      return a.created_at < b.created_at ? 1 : -1;
    });
    for (const op of sorted) {
      const arr = map.get(op.date) ?? [];
      arr.push(op);
      map.set(op.date, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const byCategory = useMemo(() => {
    const map = new Map<string, Operation[]>();
    for (const op of filtered) {
      if (op.type !== 'Расход') continue;
      const arr = map.get(op.category) ?? [];
      arr.push(op);
      map.set(op.category, arr);
    }
    return Array.from(map.entries())
      .map(([name, ops]) => ({ name, ops, total: ops.reduce((s, o) => s + o.amount, 0) }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  const currency = data?.settings.currency ?? '₽';
  const cats = data?.categories ?? [];
  const sortedCats = useMemo(
    () => [...cats].sort((a, b) => a.sort_order - b.sort_order),
    [cats],
  );

  return (
    <div style={{ padding: '26px 20px 100px' }}>
      <span className="caption">Свод операций</span>

      {/* Навигация по месяцам */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 0 14px' }}>
        <button
          onClick={() => setMonthIdx((i) => Math.min(months.length - 1, i + 1))}
          disabled={monthIdx >= months.length - 1}
          style={navBtn(monthIdx >= months.length - 1)}
        >
          <Icon name="arrowRight" size={20} style={{ transform: 'rotate(180deg)' }} />
        </button>
        <h1 style={{ fontSize: 28, fontWeight: 400, margin: 0 }}>{monthLabel(monthKey)}</h1>
        <button
          onClick={() => setMonthIdx((i) => Math.max(0, i - 1))}
          disabled={monthIdx <= 0}
          style={navBtn(monthIdx <= 0)}
        >
          <Icon name="arrowRight" size={20} />
        </button>
      </div>

      {/* Триада: приход / расход / итог */}
      <div style={{ display: 'flex', borderTop: `1px solid ${theme.colors.ink}`, borderBottom: `1px solid ${theme.colors.line}`, marginBottom: 14 }}>
        <Triad label="приход" value={`+${fmtK(totals.income)}`} color={theme.colors.green} />
        <Triad label="расход" value={`−${fmtK(totals.expense)}`} color={theme.colors.accent} sep />
        <Triad label="итог" value={`${totals.net >= 0 ? '' : '−'}${fmtK(Math.abs(totals.net))}`} color={theme.colors.ink} sep big />
      </div>

      {/* Одна идиома контролов: underline-табы + фильтр «все ▾» */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', marginBottom: 6 }}>
        {([['day', 'по дням'], ['category', 'по категориям']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setGroupBy(id)} style={tabBtn(groupBy === id)}>
            {label}
          </button>
        ))}
        <button
          onClick={() => setCatOpen((v) => !v)}
          style={{
            ...tabBtn(false),
            marginLeft: 'auto',
            color: fCat ? theme.colors.ink : theme.colors.sub,
            maxWidth: '45%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fCat || 'все'} ▾
        </button>
      </div>

      {/* Фильтр категорий — собственная сетка line-иконок (как в AddSheet) */}
      {catOpen && (
        <div
          className="rise"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 6,
            margin: '10px 0 4px',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          <button
            onClick={() => { setFCat(''); setCatOpen(false); }}
            style={gridCell(fCat === '')}
          >
            <Icon name="list" size={18} color={fCat === '' ? theme.colors.paper : theme.colors.ink} />
            все
          </button>
          {sortedCats.map((c) => {
            const on = fCat === c.name;
            return (
              <button
                key={c.name}
                onClick={() => { setFCat(on ? '' : c.name); setCatOpen(false); }}
                style={gridCell(on)}
              >
                <Icon name={categoryIconName(c)} size={18} color={on ? theme.colors.paper : theme.colors.ink} />
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{ color: theme.colors.sub, fontSize: 14, marginTop: 14 }}>
          {fCat ? 'Ничего не найдено.' : 'В этом месяце операций нет.'}
        </div>
      )}

      {groupBy === 'day' && (
        <div>
          {grouped.map(([date, ops], i) => (
            <DaySection key={date} date={date} ops={ops} categories={cats} defaultOpen={i < 2} />
          ))}
        </div>
      )}

      {groupBy === 'category' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 14 }}>
          {byCategory.map((g, i) => (
            <CategorySection
              key={g.name}
              n={String(i + 1).padStart(2, '0')}
              name={g.name}
              ops={g.ops}
              total={g.total}
              categories={cats}
              currency={currency}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Underline-таб: uppercase 11px/0.14em, активный ink + подчёрк 1.5px. */
function tabBtn(on: boolean): CSSProperties {
  return {
    background: 'transparent',
    border: 'none',
    padding: '2px 0',
    textTransform: 'uppercase',
    letterSpacing: '.14em',
    fontSize: 11,
    fontFamily: theme.font,
    cursor: 'pointer',
    color: on ? theme.colors.ink : theme.colors.sub,
    borderBottom: `1.5px solid ${on ? theme.colors.ink : 'transparent'}`,
  };
}

/** Ячейка сетки категорий (паттерн AddSheet). */
function gridCell(on: boolean): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '10px 2px',
    fontSize: 10,
    lineHeight: 1.1,
    textAlign: 'center',
    background: on ? theme.colors.ink : 'transparent',
    color: on ? theme.colors.paper : theme.colors.ink,
    border: `1px solid ${on ? theme.colors.ink : theme.colors.line}`,
    borderRadius: theme.radius.sm,
    fontFamily: theme.font,
    cursor: 'pointer',
  };
}

function navBtn(disabled: boolean): CSSProperties {
  return {
    width: 38,
    height: 38,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    color: disabled ? theme.colors.line : theme.colors.sub,
    cursor: disabled ? 'default' : 'pointer',
  };
}

function Triad({ label, value, color, sep, big }: { label: string; value: string; color: string; sep?: boolean; big?: boolean }) {
  return (
    <div style={{ flex: 1, padding: '12px 0', textAlign: 'center', borderLeft: sep ? `1px solid ${theme.colors.line}` : 'none' }}>
      <div className="caption" style={{ fontSize: 10 }}>{label}</div>
      <div className="num" style={{ fontSize: big ? 24 : 19, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

/** Секция-категория: каталожный опенер с крупным субтоталом + записи. */
function CategorySection({
  n,
  name,
  ops,
  total,
  categories,
  currency,
}: {
  n: string;
  name: string;
  ops: Operation[];
  total: number;
  categories: Category[];
  currency: string;
}) {
  const cat = categories.find((c) => c.name === name);
  const sorted = [...ops].sort((a, b) => (a.date < b.date ? 1 : -1));
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          paddingBottom: 8,
          borderBottom: `1px solid ${theme.colors.ink}`,
        }}
      >
        <span className="num" style={{ fontSize: 12, color: theme.colors.sub }}>{n}</span>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
          <Icon name={cat ? categoryIconName(cat) : 'dots'} size={16} color={theme.colors.sub} />
          {name}
        </span>
        <span className="num" style={{ fontSize: 24 }}>
          {fmtMoney(total, currency)}
        </span>
      </div>
      {sorted.map((op) => (
        <div key={op.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0 8px 30px', borderBottom: `1px solid ${theme.colors.line}` }}>
          <span style={{ fontSize: 13, color: theme.colors.sub, fontStyle: 'italic' }}>
            {op.date.slice(8, 10)}.{op.date.slice(5, 7)}{op.comment ? ` · ${op.comment}` : op.who ? ` · ${op.who}` : ''}
          </span>
          <span className="num" style={{ fontSize: 15 }}>{Math.round(op.amount).toLocaleString('ru-RU')}</span>
        </div>
      ))}
    </div>
  );
}

/** День = разворот гроссбуха: caption-заголовок ← → итог дня, снизу линия туши. */
function DaySection({
  date,
  ops,
  categories,
  defaultOpen,
}: {
  date: string;
  ops: Operation[];
  categories: Category[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const net = useMemo(() => {
    let n = 0;
    for (const o of ops) n += o.type === 'Доход' ? o.amount : -o.amount;
    return n;
  }, [ops]);

  return (
    <div>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 12,
          padding: '14px 0 8px',
          borderBottom: `1px solid ${theme.colors.ink}`,
          cursor: 'pointer',
          position: 'sticky',
          top: 0,
          background: theme.colors.bg,
          zIndex: 1,
        }}
      >
        <span className="caption">
          {formatHeaderDate(date)} · {ops.length} {plural(ops.length, 'запись', 'записи', 'записей')}
        </span>
        <span style={fraunces(24)}>
          {net > 0 ? '+' : net < 0 ? '−' : ''}
          {Math.round(Math.abs(net)).toLocaleString('ru-RU')}
        </span>
      </div>

      {open && (
        <div>
          {ops.map((op) => (
            <OpRow key={op.id} op={op} categories={categories} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Hairline-строка записи: иконка + имя (+ курсив-мерчант) ← → сумма без минуса. */
function OpRow({
  op,
  categories,
}: {
  op: Operation;
  categories: Category[];
}) {
  const { updateTx, deleteTx } = useBudget();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const cat = categories.find((c) => c.name === op.category);
  const isIncome = op.type === 'Доход';
  const note = [op.who, op.comment].filter(Boolean).join(' · ');

  return (
    <div style={{ borderBottom: `1px solid ${theme.colors.line}` }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          padding: '12px 0',
          cursor: 'pointer',
        }}
      >
        <Icon
          name={cat ? categoryIconName(cat) : 'dots'}
          size={15}
          color={theme.colors.sub}
          style={{ position: 'relative', top: 2 }}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 15,
            color: theme.colors.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {op.category}
          {note && (
            <span style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic' }}>
              {' '}· {note}
            </span>
          )}
        </span>
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 16,
            color: isIncome ? theme.colors.green : theme.colors.ink,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {isIncome ? '+' : ''}
          {Math.round(op.amount).toLocaleString('ru-RU')}
        </span>
      </div>

      {open && (
        <EditPanel
          op={op}
          categories={categories}
          onClose={() => setOpen(false)}
          onSave={async (patch) => {
            try {
              await updateTx({ id: op.id, ...patch });
              toast.show('Сохранено');
              setOpen(false);
            } catch (e) {
              toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
            }
          }}
          onDelete={async () => {
            try {
              await deleteTx(op.id);
              toast.show('Удалено');
            } catch (e) {
              toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
            }
          }}
        />
      )}
    </div>
  );
}

function EditPanel({
  op,
  categories,
  onClose,
  onSave,
  onDelete,
}: {
  op: Operation;
  categories: Category[];
  onClose: () => void;
  onSave: (patch: { date?: string; category?: string; amount?: number; who?: string; comment?: string }) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [date, setDate] = useState(op.date);
  const [category, setCategory] = useState(op.category);
  const [amountStr, setAmountStr] = useState(String(op.amount));
  const [who, setWho] = useState(op.who);
  const [comment, setComment] = useState(op.comment);
  const [busy, setBusy] = useState(false);

  const sortedCats = [...categories].sort((a, b) => a.sort_order - b.sort_order);

  function save() {
    const amount = Number(amountStr.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) return;
    setBusy(true);
    const patch: Parameters<typeof onSave>[0] = {};
    if (date !== op.date) patch.date = date;
    if (category !== op.category) patch.category = category;
    if (amount !== op.amount) patch.amount = amount;
    if (who !== op.who) patch.who = who;
    if (comment !== op.comment) patch.comment = comment;
    onSave(patch).finally(() => setBusy(false));
  }

  async function del() {
    if (!confirm(`Удалить операцию «${op.category} ${op.amount}»?`)) return;
    setBusy(true);
    try {
      await onDelete();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: '0 14px 14px', display: 'grid', gap: 8 }}>
      <Field label="Дата">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Категория">
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
          {sortedCats.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Сумма">
        <input
          type="number"
          inputMode="decimal"
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Кто">
        <input type="text" value={who} onChange={(e) => setWho(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Комментарий">
        <input
          type="text"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          style={inputStyle}
        />
      </Field>

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          onClick={save}
          disabled={busy}
          style={{
            flex: 1,
            padding: '11px 0',
            background: theme.colors.ink,
            color: theme.colors.paper,
            border: 'none',
            borderRadius: theme.radius.md,
            fontFamily: theme.font,
            fontSize: 14,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          {busy ? '...' : 'Сохранить'}
        </button>
        <button
          onClick={onClose}
          disabled={busy}
          style={{
            padding: '11px 14px',
            background: 'transparent',
            color: theme.colors.sub,
            border: `1px solid ${theme.colors.line}`,
            borderRadius: theme.radius.md,
            fontFamily: theme.font,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Отмена
        </button>
        <button
          onClick={del}
          disabled={busy}
          aria-label="Удалить"
          style={{
            padding: '11px 14px',
            background: 'transparent',
            color: theme.colors.red,
            border: `1px solid ${theme.colors.line}`,
            borderRadius: theme.radius.md,
            fontFamily: theme.font,
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Icon name="trash" size={16} />
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: theme.colors.sub,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  fontFamily: theme.font,
  fontSize: 15,
  background: theme.colors.card,
  color: theme.colors.ink,
  outline: 'none',
  boxSizing: 'border-box',
};

function plural(n: number, a: string, b: string, c: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return c;
  if (m10 === 1) return a;
  if (m10 >= 2 && m10 <= 4) return b;
  return c;
}

function formatHeaderDate(iso: string): string {
  // "2026-06-09" → "9 июня 2026" или "сегодня" / "вчера" если применимо
  const d = parseIsoLocal(iso);
  if (!d) return iso;
  const today = new Date();
  const yToday = today.getFullYear();
  const mToday = today.getMonth();
  const dToday = today.getDate();

  if (d.getFullYear() === yToday && d.getMonth() === mToday && d.getDate() === dToday) return 'Сегодня';
  const y2 = new Date(yToday, mToday, dToday - 1);
  if (d.getFullYear() === y2.getFullYear() && d.getMonth() === y2.getMonth() && d.getDate() === y2.getDate()) return 'Вчера';

  const months = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
  const sameYear = d.getFullYear() === yToday;
  return `${d.getDate()} ${months[d.getMonth()]}${sameYear ? '' : ` ${d.getFullYear()}`}`;
}

function parseIsoLocal(s: string): Date | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
